import { keepPreviousData } from "@tanstack/react-query";

import type {
    MarketResearchMarketAnalysisLobSegmentsListParams,
    ProWebsiteFeaturesLobsDtoLobSegmentsResponse,
} from "@similarweb/pro-api-client";

import { apiClient } from "app/modules/api-client";
import type { Data } from "app/modules/api-client/queryTypes";
import { useApiQuery } from "app/modules/api-client/useApiQuery";
import { hasCustomLobsSegmentsClaim } from "app/pages/segments/config/segmentsConfigHelpers";

type ApiResponse = Data<"IndustryAnalysis", "marketResearchMarketAnalysisLobSegmentsList">;

export const useLinesOfBusinessQuery = (searchQuery: string, select?: (data: ApiResponse) => ApiResponse) => {
    const hasAccess = hasCustomLobsSegmentsClaim();
    const normalizedQuery = (searchQuery ?? "").trim();

    return useApiQuery(
        {
            tag: "IndustryAnalysis",
            methodName: "marketResearchMarketAnalysisLobSegmentsList",
            options: {
                enabled: hasAccess,
                select,
                placeholderData: keepPreviousData,
            },
        },
        { filter: normalizedQuery } satisfies MarketResearchMarketAnalysisLobSegmentsListParams,
    );
};

/**
 * Fetches Lines of Business segments by their segment IDs.
 * This is a non-react-query function for programmatic data fetching.
 *
 * Accepts segment IDs in internal format (with $LOB_ prefix) and automatically
 * strips the prefix before sending to the API.
 *
 * @param linesOfBusinessSegmentIds - Array of Lines of Business segment IDs (e.g., '$LOB_domain.com;footwear')
 * @returns Promise resolving to Lines of Business segments response data
 *
 * @example
 * const ids = ['$LOB_hubspot.com;Marketing', '$LOB_salesforce.com;Sales'];
 * const segments = await fetchLinesOfBusinessSegmentsByIds(ids);
 */
export const fetchLinesOfBusinessSegmentsByIds = async (
    linesOfBusinessSegmentIds: string[],
): Promise<ProWebsiteFeaturesLobsDtoLobSegmentsResponse> => {
    if (!linesOfBusinessSegmentIds.length) {
        return null;
    }

    // Strip the $LOB_ prefix from segment IDs as the API expects format: "domain.com;businessLine"
    const apiFormattedIds = linesOfBusinessSegmentIds.map((id) => id.replace(/^\$LOB_/, ""));

    return apiClient.IndustryAnalysis.marketResearchMarketAnalysisLobSegmentsList({
        id: apiFormattedIds,
    } satisfies MarketResearchMarketAnalysisLobSegmentsListParams);
};
